import React, { FC, SyntheticEvent, useState } from 'react';

import { Input, LoadingOverlay, PhoneInput, PrimaryButton } from 'components';
import { ChevronRightIcon } from 'shared/icons';
import styles from './LoyaltyJoinForm.module.scss';

import { ErrorMessages } from 'shared/constants/errorMessages';
import {
    CustomerStatus,
    GetCustomerStatusResponse,
    TCustomerStatus,
} from 'shared/api/v2/customers/customers.types';
import { getCustomerStatus } from 'shared/api/v2/customers/customers.client';
import { TVerificationModalError } from '../VerificationCodeModal/VerificationCodeModal';
import { triggerPhoneVerification } from 'shared/utils/phoneVerification';
import { logger } from 'shared/utils/logger/logger';

export interface ILoyaltyJoinFormProps {
    customerEmail: string;
    customerPhoneNumber: string;
    setCustomerEmail: (email: string) => void;
    setCustomerPhoneNumber: (phoneNumber: string) => void;
    isVerificationModalOpen: boolean;
    setIsVerificationModalOpen: (
        isOpen: boolean,
        errorMessage?: TVerificationModalError
    ) => void;
    merchantId: string;
    merchantPhoneNumber: string | null;
    setCustomerId: (customerId: string | null) => void;
    setCustomerStatus: (status: TCustomerStatus) => void;
    businessName: string;
    isRootMerchant?: boolean;
    setIsCustomerModalOpen?: (isOpen: boolean) => void;
}

const LoyaltyJoinForm: FC<ILoyaltyJoinFormProps> = ({
    customerEmail,
    customerPhoneNumber,
    merchantId,
    businessName,
    merchantPhoneNumber,
    setIsVerificationModalOpen,
    setCustomerEmail,
    setCustomerPhoneNumber,
    setCustomerId,
    setCustomerStatus,
    isRootMerchant = false,
    setIsCustomerModalOpen,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldError, setFieldError] = useState({ email: '', phone: '' });

    if (!merchantId) {
        return null;
    }

    const onEmailChange = (value: string): void => {
        if (fieldError.email) {
            setFieldError((s) => ({ ...s, email: '' }));
        }
        setCustomerEmail(value);
    };

    const onPhoneNumberChange = (value: string): void => {
        if (fieldError.phone) {
            setFieldError((s) => ({ ...s, phone: '' }));
        }
        setCustomerPhoneNumber(value);
    };

    const onClear = (): void => {
        setCustomerEmail('');
    };

    const handlePhoneVerification = async () => {
        await triggerPhoneVerification(
            customerPhoneNumber,
            merchantPhoneNumber,
            setIsVerificationModalOpen
        );
    };

    const fetchCustomerStatus = async (identifier: {
        email?: string;
        phone_number?: string;
    }): Promise<GetCustomerStatusResponse | null> => {
        const { data: customerInfo, error } = await getCustomerStatus({
            ...identifier,
            merchantId,
        });
        if (customerInfo) return customerInfo;
        if (error) {
            setError(error?.generic || '');
            setFieldError({
                email: error?.email || '',
                phone: error?.phone_number || '',
            });
        }
        return null;
    };

    const onSubmit = async (evt: SyntheticEvent<HTMLFormElement>) => {
        evt.preventDefault();
        setIsLoading(true);
        setError('');
        setFieldError({ email: '', phone: '' });

        try {
            const customerInfo = await fetchCustomerStatus({
                email: customerEmail,
                phone_number: customerPhoneNumber,
            });

            if (customerInfo === null) {
                return;
            }

            setCustomerId(customerInfo.customer_id);
            setCustomerStatus(customerInfo.status);

            if (customerInfo.status === CustomerStatus.CONFLICT) {
                setError(
                    'This phone number or email is already part of our loyalty program'
                );
            } else {
                // For root merchants, open customer modal first to select location
                // Verification will be triggered after location selection
                if (isRootMerchant && setIsCustomerModalOpen) {
                    setIsCustomerModalOpen(true);
                } else {
                    await handlePhoneVerification();
                }
            }
        } catch (error) {
            logger.error(
                'Loyalty join form submission failed',
                'Frontend:LoyaltyJoinForm',
                {
                    merchantId,
                    isRootMerchant,
                    hasMerchantPhoneNumber: Boolean(merchantPhoneNumber),
                    error,
                }
            );
            setError(ErrorMessages.DEFAULT);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className={styles.Form}>
            <PhoneInput
                value={customerPhoneNumber}
                onChange={onPhoneNumberChange}
                label="Phone number"
                required
                errorMessage={fieldError.phone}
            />
            <p className={styles.TermsAndPolicy}>
                By clicking the Sign up button below, you consent to receive
                marketing messages from {businessName}. Reply UNSUBSCRIBE to
                opt-out.{' '}
                <a href="https://www.spoton.com/legal/">
                    Terms &amp; Conditions
                </a>{' '}
                and{' '}
                <a href="https://www.spoton.com/legal/consumer-privacy/">
                    Privacy Policy
                </a>{' '}
                apply.
            </p>
            <Input
                hasIcon
                value={customerEmail}
                onChange={onEmailChange}
                onClear={onClear}
                type="email"
                label="Email"
                required
                placeholder="ex: john.smith@email.com"
                className={styles.Input}
                errorMessage={fieldError.email}
            />
            <PrimaryButton
                disabled={!customerPhoneNumber || !customerEmail || isLoading}
                type="submit"
                icon={<ChevronRightIcon />}
                className={styles.SubmitButton}
            >
                Sign up
            </PrimaryButton>
            {error && <p className={styles.Error}>{error}</p>}
            {isLoading && <LoadingOverlay />}
        </form>
    );
};

export default LoyaltyJoinForm;
